import { Fragment, useMemo, type ReactNode } from 'react';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import { safeRichTextLink } from './rich-text-dom';

interface MarkdownNode {
  type: string;
  children?: MarkdownNode[];
  value?: string;
  url?: string;
  alt?: string;
  depth?: number;
  ordered?: boolean;
  start?: number;
  identifier?: string;
}
const parser = unified().use(remarkParse).use(remarkGfm);
function parse(source: string) {
  if (source.length > 65536) throw new Error('MARKDOWN_TOO_LARGE');
  const root = parser.parse(source) as MarkdownNode;
  let count = 0;
  const check = (node: MarkdownNode, depth: number) => {
    if (++count > 10000 || depth > 40) throw new Error('MARKDOWN_TOO_LARGE');
    for (const child of node.children ?? []) check(child, depth + 1);
  };
  check(root, 0);
  return root;
}
function link(href: string | undefined, children: ReactNode) {
  try {
    return (
      <a href={safeRichTextLink(href ?? '').href} rel="noopener noreferrer">
        {children}
      </a>
    );
  } catch {
    return children;
  }
}
function render(node: MarkdownNode, definitions: Map<string, string>): ReactNode {
  const children = (node.children ?? []).map((child, index) => (
    <Fragment key={index}>{render(child, definitions)}</Fragment>
  ));
  switch (node.type) {
    case 'root':
      return children;
    case 'text':
    case 'html':
      return node.value;
    case 'paragraph':
      return <p>{children}</p>;
    case 'strong':
      return <strong>{children}</strong>;
    case 'emphasis':
      return <em>{children}</em>;
    case 'delete':
      return <del>{children}</del>;
    case 'inlineCode':
      return <code>{node.value}</code>;
    case 'code':
      return (
        <pre>
          <code>{node.value}</code>
        </pre>
      );
    case 'heading': {
      const tag =
        node.depth === 1
          ? 'h1'
          : node.depth === 2
            ? 'h2'
            : node.depth === 3
              ? 'h3'
              : node.depth === 4
                ? 'h4'
                : node.depth === 5
                  ? 'h5'
                  : 'h6';
      const Heading = tag;
      return <Heading>{children}</Heading>;
    }
    case 'list':
      return node.ordered ? <ol start={node.start}>{children}</ol> : <ul>{children}</ul>;
    case 'listItem':
      return <li>{children}</li>;
    case 'blockquote':
      return <blockquote>{children}</blockquote>;
    case 'break':
      return <br />;
    case 'thematicBreak':
      return <hr />;
    case 'link':
      return link(node.url, children);
    case 'linkReference':
      return link(definitions.get(node.identifier ?? ''), children);
    case 'image':
    case 'imageReference':
      return node.alt;
    case 'definition':
      return null;
    case 'table': {
      const rows = node.children ?? [];
      const cells = (row: MarkdownNode, header: boolean) =>
        row.children?.map((cell, index) =>
          header ? (
            <th key={index}>{render(cell, definitions)}</th>
          ) : (
            <td key={index}>{render(cell, definitions)}</td>
          ),
        );
      return (
        <table>
          <thead>{rows[0] && <tr>{cells(rows[0], true)}</tr>}</thead>
          <tbody>
            {rows.slice(1).map((row, index) => (
              <tr key={index}>{cells(row, false)}</tr>
            ))}
          </tbody>
        </table>
      );
    }
    default:
      return children;
  }
}
export function Markdown({ source, fallback }: { source: string; fallback: ReactNode }) {
  const content = useMemo(() => {
    try {
      const root = parse(source);
      const definitions = new Map(
        (root.children ?? [])
          .filter((node) => node.type === 'definition')
          .map((node) => [node.identifier ?? '', node.url ?? '']),
      );
      return render(root, definitions);
    } catch {
      return fallback;
    }
  }, [source, fallback]);
  return <div className="pc-prose">{content}</div>;
}
